import { useEffect, useState } from 'react';
import Animated from '../../components/essence/animated/animated';
import useLoaderDisplay from '../../shared/hooks/loader-display';
import { useTodoMutations } from '../../shared/tanstack/todos/mutations';
import { useTodoQueries } from '../../shared/tanstack/todos/queries';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Todo as TodoItem } from '../../shared/models/todo';
import AddTodoForm from './components/add-todo-form/page';
import accountService from '../../shared/services/account-service';
import { Share } from '@capacitor/share';
import ListTodo from './components/list-todo/page';

interface Props {
	circleId: string;
	circleName: string;
}
export default function Todo({ circleId, circleName }: Props) {
	const { toggleToDoMutation, addToDoMutation, deleteToDoMutation } = useTodoMutations(circleId);
	const { mutate: toggleToDo } = useMutation(toggleToDoMutation);
	const { mutate: addToDo } = useMutation(addToDoMutation);
	const { mutate: deleteToDo } = useMutation(deleteToDoMutation);

	const { toDoQuery } = useTodoQueries();
	const { data: todos, isLoading } = useQuery(toDoQuery(circleId));
	useLoaderDisplay({ key: 'getToDos', isLoading });

	const [activeTodos, setActiveTodos] = useState<TodoItem[]>(
		todos?.filter(todo => todo.status === 'PENDING').sort((a, b) => a.order - b.order) ?? [],
	);
	const [completedTodos, setCompletedTodos] = useState<TodoItem[]>(
		todos?.filter(todo => todo.status === 'COMPLETED').sort((a, b) => a.order - b.order) ?? [],
	);
	const { reorderToDoMutation, editToDoMutation } = useTodoMutations(circleId);
	const { mutate: reorderToDo } = useMutation(reorderToDoMutation);
	const { mutate: editToDo } = useMutation(editToDoMutation);

	useEffect(() => {
		if (todos) {
			setActiveTodos(todos.filter(todo => todo.status === 'PENDING').sort((a, b) => a.order - b.order));
			setCompletedTodos(todos.filter(todo => todo.status === 'COMPLETED').sort((a, b) => a.order - b.order));
		}
	}, [todos]);

	const shareToDos = async () => {
		let shareText = `Here is a list of todos for ${circleName}. Try out Circles to manage your todos, expense and much more.\n\n`;
		activeTodos.forEach(todo => {
			shareText += `[   ]  ${todo.name}\n`;
		});

		completedTodos.forEach(todo => {
			shareText += `[✓]  ${todo.name}\n`;
		});
		const account = accountService.getAccount();
		const url = account?.referralLink ?? 'https://circlesco.app.link/share';
		await Share.share({ url: url, text: shareText });
	};

	const addToDoRequest = (newTodo: string) => {
		addToDo({
			circleId,
			name: newTodo,
		});
	};

	const handleToggleCompletion = (id: string) => {
		toggleToDo({ id, circleId });
	};

	const handleRemoveTodo = (id: string) => {
		deleteToDo({ id, circleId });
	};

	const handleReorderTodos = (todos: TodoItem[]) => {
		if (todos.length > 0) {
			const todo = todos[0];
			if (todo.status === 'PENDING') {
				reorderToDo([...todos, ...completedTodos]);
			} else {
				reorderToDo([...activeTodos, ...todos]);
			}
		}
	};

	const handleEditTodo = (todo: TodoItem, text: string) => {
		editToDo({ id: todo.id, name: text });
	};
	return (
		<>
			<Animated>
				<AddTodoForm
					onAddTodo={addToDoRequest}
					onShare={() => {
						shareToDos().catch(() => {
							// Do nothing
						});
					}}
				/>
			</Animated>
			<ListTodo
				showIndex={false}
				todos={activeTodos}
				setTodos={setActiveTodos}
				onToggle={handleToggleCompletion}
				onRemove={handleRemoveTodo}
				onReorder={handleReorderTodos}
				onEdit={handleEditTodo}
			/>

			<ListTodo
				showIndex={false}
				todos={completedTodos}
				setTodos={setCompletedTodos}
				onToggle={handleToggleCompletion}
				onRemove={handleRemoveTodo}
				onReorder={handleReorderTodos}
				onEdit={handleEditTodo}
			/>
		</>
	);
}
